import { Component, type ErrorInfo, type ReactNode } from "react";
import { Link } from "react-router";

// The two terminal pages the SPA was missing. Before this, a malformed `:id` in a URL threw a
// `ParseError` straight out of `DocumentView`'s render pass (`DocumentId.make`), and — with no error
// boundary anywhere in the tree — React unmounted the whole app and left a white page with nothing but
// a console trace. Both halves are fixed here: a 404 for the case the app can NAME (a URL that cannot
// address a document), and a boundary for every case it cannot.

// Exported for `unauthenticated-page.tsx` (Unit 5): a third full-page state sharing this exact look,
// past the "three genuine duplicates" threshold for extraction.
export const PAGE =
  "flex h-screen flex-col items-center justify-center gap-4 bg-zinc-100 px-6 text-center";
export const HEADING = "text-lg font-semibold text-zinc-800";
export const BODY = "max-w-md text-sm text-zinc-600";
export const ACTION =
  "rounded-lg border border-zinc-300 bg-white px-4 py-2 text-sm font-medium text-zinc-800 " +
  "shadow-[0_1px_2px_rgba(0,0,0,0.05)] hover:bg-zinc-50";

// A URL that cannot address a document: a malformed id (the chat agent's dropped `.md`), or a hand-typed
// path. The reviewer's way out is the index — the one route that is always reachable and always lists
// what does exist, so it beats a "go back" that would land them on the same dead link.
export const NotFoundPage = ({ detail }: { readonly detail?: string }) => (
  <div className={PAGE}>
    <h1 className={HEADING}>Not found</h1>
    <p className={BODY}>
      {detail ?? "This link doesn’t point at a document. It may be stale, or mistyped."}
    </p>
    <Link to="/curricula" className={ACTION}>
      All curricula
    </Link>
  </div>
);

// The catch-all. A class component because `componentDidCatch`/`getDerivedStateFromError` have no hook
// equivalent — React exposes render-phase error recovery only through this API. Deliberately NOT
// react-router's `errorElement`: that is a data-router feature, and `App.tsx` uses the declarative
// `<Routes>` tree, so a boundary component is the mechanism actually available here.
//
// It renders a reload affordance rather than a "retry" that re-renders the same broken tree in place: an
// uncaught render error usually means some store is in a state this build can't render (the
// `applyChangeSetJson` RangeError in `routes.tsx`'s `getRule` comment was exactly that), and a full
// reload re-seeds every store from the server's authoritative head. The message is deliberately not the
// error's own text — that is for the console, not the reviewer — but the error IS logged, so a
// production white-page is still diagnosable from a session's console.
export class AppErrorBoundary extends Component<
  { readonly children: ReactNode },
  { readonly failed: boolean }
> {
  override state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  override componentDidCatch(error: unknown, info: ErrorInfo) {
    console.error("Unhandled render error", error, info.componentStack);
  }

  override render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className={PAGE}>
        <h1 className={HEADING}>Something went wrong</h1>
        <p className={BODY}>
          This view hit an error it couldn’t recover from. Reloading re-reads the document from the
          server; nothing you’d landed is lost.
        </p>
        <button type="button" onClick={() => window.location.reload()} className={ACTION}>
          Reload
        </button>
      </div>
    );
  }
}
